import { Children, isValidElement, type ReactNode } from "react";
import { Popover } from "@base-ui/react/popover";
import Accordion from "../Accordion";
import { IconAdjustmentsHorizontal, IconPlus } from "@tabler/icons-react";
import Button from "../Button";
import SearchInput from "../SearchInput";
import Title from "../Title";
import useDialogPortalContainer from "../../../hooks/use-dialog-portal-container";
import styles from "./FilterBar.module.css";
import CountBadge from "../CountBadge.tsx";
import Card from "../Card.tsx";
import { Separator } from "@base-ui/react";

/** Shape of the FilterSection/FilterRangeSection children that FilterBar reads
 * to decide which sections start expanded (their `title` is the accordion value). */
interface FilterSectionLikeProps {
  title: string;
  defaultOpen?: boolean;
  values?: string[];
  minValue?: string;
  maxValue?: string;
}

function openSectionValues(children: ReactNode): string[] {
  return Children.toArray(children)
    .filter(isValidElement)
    .map((child) => child.props as FilterSectionLikeProps)
    .filter(
      (p) =>
        !!p.defaultOpen ||
        (p.values?.length ?? 0) > 0 ||
        !!p.minValue ||
        !!p.maxValue,
    )
    .map((p) => p.title);
}

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  activeFiltersCount: number;
  onClearFilters: () => void;
  addLabel?: string;
  onAdd?: () => void;
  leftActions?: ReactNode;
  children: ReactNode;
}

export default function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Rechercher...",
  activeFiltersCount,
  onClearFilters,
  addLabel,
  onAdd,
  leftActions,
  children,
}: FilterBarProps) {
  const renderedLeftActions =
    leftActions ??
    (onAdd ? (
      <Button icon={IconPlus} onClick={onAdd}>
        {addLabel}
      </Button>
    ) : null);
  const { container, triggerRef } = useDialogPortalContainer();

  return (
    <div className={styles.bar}>
      {renderedLeftActions && (
        <div className={styles.leftActions}>{renderedLeftActions}</div>
      )}
      <div className={styles.spacer} />
      <div className={styles.search}>
        <SearchInput
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={searchPlaceholder}
        />
      </div>
      <Popover.Root>
        <Popover.Trigger
          ref={triggerRef}
          aria-label="Filtrer"
          className={styles.filterButton}
          nativeButton={false}
          render={
            <div>
              <Button
                variant="secondary"
                icon={IconAdjustmentsHorizontal}
                aria-label="Filtrer"
              >
                <span className={styles.filterLabel}>Filtres</span>
              </Button>
              {activeFiltersCount > 0 && (
                <CountBadge
                  count={activeFiltersCount}
                  className={styles.countBadgeFloating}
                />
              )}
            </div>
          }
        />
        <Popover.Portal container={container}>
          <Popover.Positioner
            sideOffset={8}
            align="end"
            className={styles.positioner}
          >
            <Popover.Popup
              className={styles.popup}
              render={
                <Card padding="none">
                  <div className={styles.popupHeader}>
                    <IconAdjustmentsHorizontal size={20} />
                    <Title as="span" variant="deca" weight="bold">
                      Filtres
                    </Title>
                  </div>
                  <Separator
                    orientation="horizontal"
                    className={styles.divider}
                  />
                  <div className={styles.popupBody}>
                    <Accordion
                      multiple
                      divider="border"
                      defaultValue={openSectionValues(children)}
                    >
                      {children}
                    </Accordion>
                  </div>
                  <Separator
                    orientation="horizontal"
                    className={styles.divider}
                  />
                  <div className={styles.popupActions}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClearFilters}
                      disabled={activeFiltersCount === 0}
                    >
                      Effacer les filtres
                    </Button>
                    <Popover.Close
                      render={<Button type="button">Valider</Button>}
                    />
                  </div>
                </Card>
              }
            />
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
